// TanStack Query hook for the debtor-detail Subsidy Ledger tab (Slice H).

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { ReconciliationService } from "@/api";
import type { reconciliation_SubsidyLedgerResponseDTO } from "@/api";

export const subsidyLedgerKeys = {
  all: ["reconciliation", "subsidy-ledger"] as const,
  // orgId is part of the key so a tenant switch can't serve another org's
  // cached ledger (frontend.md: tenant-scoped query keys MUST include the
  // active orgId). limit/offset are part of the key so each page is cached
  // separately — the hook passes them to the API, so omitting them would
  // collide pages.
  detail: (
    orgId: string | null,
    debtorId: string | null,
    period: { start: string; end: string } | null,
    filter: { status?: string; q?: string; sort?: string } | null,
    limit?: number,
    offset?: number,
  ) =>
    [
      ...subsidyLedgerKeys.all,
      "detail",
      orgId,
      debtorId,
      period,
      filter,
      limit,
      offset,
    ] as const,
};

/** Triage filter values the ledger endpoint accepts. */
const LEDGER_STATUSES = ["all", "outstanding", "paid"] as const;

/** Sort keys the ledger endpoint accepts. */
const LEDGER_SORTS = [
  "period_desc",
  "period_asc",
  "child_asc",
  "child_desc",
  "child_id_asc",
  "child_id_desc",
  "expected_asc",
  "expected_desc",
  "paid_asc",
  "paid_desc",
  "outstanding_asc",
  "outstanding_desc",
] as const;

/**
 * Narrow the caller's free-string filter/sort to what the generated service declares.
 * The tab's controls only produce these literals; anything else (e.g. the
 * child-totals-only "overpaid" status, which this endpoint rejects with a 400)
 * degrades to `undefined`, i.e. the server default.
 */
const toLedgerStatus = (status: string | undefined) =>
  LEDGER_STATUSES.find((candidate) => candidate === status);
const toLedgerSort = (sort: string | undefined) =>
  LEDGER_SORTS.find((candidate) => candidate === sort);

export interface UseSubsidyLedgerParams {
  debtorId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** Triage filter: "all" | "outstanding" | "paid" (omit/"" = all). */
  status?: string;
  /** Child search: internal id / first / last / full name. */
  q?: string;
  /** Sort key, e.g. "outstanding_desc" (omit = server default period_desc). */
  sort?: string;
  limit?: number;
  offset?: number;
}

export function useSubsidyLedger(params: UseSubsidyLedgerParams) {
  const { orgId } = useAuth();
  const enabled =
    params.debtorId !== null &&
    params.debtorId !== "" &&
    params.periodStart !== null &&
    params.periodStart !== "" &&
    params.periodEnd !== null &&
    params.periodEnd !== "";
  return useQuery<reconciliation_SubsidyLedgerResponseDTO>({
    queryKey: subsidyLedgerKeys.detail(
      orgId ?? null,
      params.debtorId,
      enabled
        ? { start: params.periodStart as string, end: params.periodEnd as string }
        : null,
      { status: params.status, q: params.q, sort: params.sort },
      params.limit,
      params.offset,
    ),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsSubsidyLedger(
        params.debtorId as string,
        params.periodStart ?? undefined,
        params.periodEnd ?? undefined,
        toLedgerStatus(params.status),
        params.q || undefined,
        toLedgerSort(params.sort),
        params.limit,
        params.offset,
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}
